import { type VendorCatalogBrowseResult } from '@choco-admin/types';

/**
 * Decision helpers for the `useVendorCatalogBrowse` state machine, extracted as
 * pure functions so the tricky seeding / stale-drop rules can be unit-tested
 * exhaustively without simulating Apollo's cache and network internals.
 */

interface ShouldSeedPageOneArgs {
  /** Whether the active page-1 query is still in flight. */
  loading: boolean;
  /** The mapped page-1 result for the current query. */
  result: VendorCatalogBrowseResult | null;
  /** Signature (query identity) of the current render. */
  signature: string;
  /** Signature that last seeded page 1, or null after a reset. */
  seededSignature: string | null;
  /** The exact mapped result object last seeded, or null after a reset. */
  seededResult: VendorCatalogBrowseResult | null;
}

/**
 * Whether a settled page-1 network result should (re)seed the accumulated list.
 *
 * Apollo 3.14 replays the previous result synchronously (with `loading: true`)
 * when a query re-activates with unchanged variables. Latching on that replay
 * would freeze a stale page 1 and throw away the fresh `no-cache` response, so
 * we only seed once the network has settled (`loading === false`) and the
 * mapped result is a fresh object we have not already seeded for this
 * signature. A fresh completion for the current signature therefore always
 * replaces page 1 (and, at the call site, resets any accumulated "Load more"
 * pages, since they were relative to the previous page 1).
 */
export const shouldSeedPageOne = ({
  loading,
  result,
  signature,
  seededSignature,
  seededResult,
}: ShouldSeedPageOneArgs): boolean => {
  if (loading) {
    return false;
  }
  if (!result || result.kind !== 'success') {
    return false;
  }
  if (seededSignature === signature && seededResult === result) {
    return false;
  }
  return true;
};

/**
 * Whether an in-flight "Load more" response still belongs to the live query.
 *
 * A response captured under `requestSignature` must be dropped when the live
 * signature has since changed (the operator switched sub-catalog filter, search
 * term, or browse/search mode mid-flight). Applying it would append foreign
 * rows and overwrite `pageInfo` with a cursor from the previous result set.
 */
export const isLoadMoreResponseFresh = (
  requestSignature: string,
  liveSignature: string
): boolean => requestSignature === liveSignature;
